import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupIcon from '@mui/icons-material/Group';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';

const CYCLE_STATUS_LABEL = {
  planning: 'Planning',
  active: 'Active',
  cooldown: 'Cool-down',
  complete: 'Complete',
};

// Only an active cycle earns the filled green chip; everything else stays quiet
// so a grid of cards reads at a glance where the live work is.
const statusChipProps = (status) => (status === 'active'
  ? { color: 'success', variant: 'filled' }
  : { color: 'default', variant: 'outlined' });

const formatRange = (cycle) => {
  if (!cycle?.startDate && !cycle?.endDate) return null;
  const fmt = (value) => new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  if (cycle.startDate && cycle.endDate) return `${fmt(cycle.startDate)} – ${fmt(cycle.endDate)}`;
  return fmt(cycle.startDate || cycle.endDate);
};

/**
 * A summary of one cycle. The whole card is a link into the Shape Up hill chart
 * with this cycle preselected, so a click goes straight to the work. Admins get
 * a ⋮ menu to archive (reversible) or delete (permanent) the cycle; those
 * controls stop propagation so they never trigger the card's navigation.
 */
const CycleCard = ({ cycle, organizationId, teamName, canManage = false, onArchive, onRestore, onDelete }) => {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isArchived = Boolean(cycle.archivedAt);

  const range = formatRange(cycle);
  const betCount = cycle.betCount ?? 0;

  const openHill = () => {
    const params = new URLSearchParams();
    if (organizationId) params.set('org', organizationId);
    params.set('cycle', cycle.id);
    navigate(`/shape-up?${params.toString()}`);
  };

  // The menu button lives inside the CardActionArea, so every handler has to
  // stop the click from bubbling up into the navigation.
  const openMenu = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };
  const closeMenu = (event) => {
    if (event) event.stopPropagation();
    setMenuAnchor(null);
  };

  const handleArchiveToggle = (event) => {
    closeMenu(event);
    if (isArchived) onRestore?.(cycle);
    else onArchive?.(cycle);
  };

  const handleDeleteClick = (event) => {
    closeMenu(event);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    setConfirmDelete(false);
    onDelete?.(cycle);
  };

  return (
    <Card sx={{ height: '100%', opacity: isArchived ? 0.75 : 1 }}>
      <CardActionArea onClick={openHill} sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 0, flexGrow: 1 }}>
              {cycle.name}
            </Typography>
            <Chip
              size="small"
              label={CYCLE_STATUS_LABEL[cycle.status] || cycle.status}
              {...statusChipProps(cycle.status)}
            />
            {isArchived && (
              <Chip size="small" variant="outlined" label="Archived" />
            )}
            {canManage && (
              <IconButton
                size="small"
                aria-label="Cycle actions"
                onClick={openMenu}
                sx={{ mt: -0.5, mr: -0.5 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {range && (
            <Typography variant="body2" color="text.secondary">{range}</Typography>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              {betCount} {betCount === 1 ? 'bet' : 'bets'}
            </Typography>
            {cycle.teamId && teamName && (
              <Chip
                size="small"
                variant="outlined"
                icon={<GroupIcon fontSize="small" />}
                label={teamName}
              />
            )}
          </Stack>
        </CardContent>
      </CardActionArea>

      {canManage && (
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
          <MenuItem onClick={handleArchiveToggle}>
            {isArchived ? (
              <>
                <UnarchiveOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                Restore
              </>
            ) : (
              <>
                <ArchiveOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                Archive
              </>
            )}
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>
      )}

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete {cycle.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes the cycle and its {betCount} {betCount === 1 ? 'bet' : 'bets'},
            along with their scopes. This cannot be undone. To keep the data but hide the cycle,
            archive it instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CycleCard;
